<template>
  <div>
    <div v-if="showClientUI" class="secure-container analyses-page">
      <div class="secure-content-wrapper">
        <div :class="{ blurred: !accessGranted }" class="secure-content">
          <div v-if="accessGranted" class="client-page analyses-page__inner">
            <header class="client-page__header">
              <h1 class="client-page__title">Vos analyses</h1>
            </header>

            <p v-if="duoLoadError" class="client-alert client-alert--error" role="alert">{{ duoLoadError }}</p>

            <!-- KPIs réels (pas de chiffres inventés) -->
            <div class="analyses-kpis" aria-label="Indicateurs patrimoniaux">
              <article class="analyses-kpi">
                <p class="analyses-kpi__label">Patrimoine net</p>
                <p class="analyses-kpi__value">
                  <template v-if="kpiReady && kpis.valeurPatrimoine != null">
                    {{ formatMoney(kpis.valeurPatrimoine) }}&nbsp;F&nbsp;CFA
                  </template>
                  <template v-else>—</template>
                </p>
                <p class="analyses-kpi__meta" :class="kpiMetaClass(kpis.valeurPatrimoine)">
                  {{ kpiReady && kpis.valeurPatrimoine != null ? 'Issu de votre dernier bilan' : 'Disponible après publication' }}
                </p>
              </article>
              <article class="analyses-kpi">
                <p class="analyses-kpi__label">Solde budgétaire</p>
                <p class="analyses-kpi__value">
                  <template v-if="kpiReady && kpis.soldeBudgetaire != null">
                    {{ formatMoney(kpis.soldeBudgetaire) }}&nbsp;F&nbsp;CFA
                  </template>
                  <template v-else>—</template>
                </p>
                <p
                  class="analyses-kpi__meta"
                  :class="kpis.soldeBudgetaire < 0 ? 'analyses-kpi__meta--warn' : 'analyses-kpi__meta--ok'"
                >
                  <template v-if="kpiReady && kpis.soldeBudgetaire != null">
                    {{ kpis.soldeBudgetaire < 0 ? 'À surveiller' : 'Revenus − dépenses' }}
                  </template>
                  <template v-else>Disponible après publication</template>
                </p>
              </article>
              <article class="analyses-kpi">
                <p class="analyses-kpi__label">Documents disponibles</p>
                <p class="analyses-kpi__value">{{ documentsCount }}</p>
                <p class="analyses-kpi__meta analyses-kpi__meta--link">
                  <router-link to="/analyse-patrimoniale/mes-recommandations">
                    Voir mes recommandations →
                  </router-link>
                </p>
              </article>
            </div>

            <!-- Offre Duo : onglets -->
            <div v-if="typeOffre === 'duo'" class="duo-tabs" role="tablist" aria-label="Bilans offre duo">
              <button type="button" class="duo-tab" :class="{ active: activeTab === 'conjoint1' }" @click="activeTab = 'conjoint1'">
                Conjoint 1
              </button>
              <button type="button" class="duo-tab" :class="{ active: activeTab === 'conjoint2' }" @click="activeTab = 'conjoint2'">
                Conjoint 2
              </button>
              <button type="button" class="duo-tab" :class="{ active: activeTab === 'couple' }" @click="activeTab = 'couple'">
                Couple
              </button>
            </div>

            <h2 class="client-section__label">Documents</h2>

            <!-- Duo: Conjoint 1 -->
            <div v-if="typeOffre === 'duo' && activeTab === 'conjoint1'" class="analyses-docs">
              <div v-if="loadingDuo" class="doc-card doc-card--loading">Chargement du bilan conjoint 1…</div>
              <article v-else-if="duoConjoint1" class="doc-card">
                <div class="doc-card__thumb" aria-hidden="true">
                  <span class="doc-card__thumb-bar" />
                  <span class="doc-card__thumb-body" />
                </div>
                <div class="doc-card__body">
                  <span class="doc-badge" :class="statusBadgeClass(duoConjoint1)">
                    {{ statusLabel(duoConjoint1) }}
                  </span>
                  <h3 class="doc-card__title">
                    Analyse patrimoniale — conjoint 1 · {{ formatMonthYear(duoConjoint1.date_modification || duoConjoint1.date_creation) }}
                  </h3>
                  <p class="doc-card__meta">
                    Bilan individuel · mise à jour {{ relativeDate(duoConjoint1.date_modification || duoConjoint1.date_creation) }}
                  </p>
                  <div class="doc-card__actions">
                    <button type="button" class="doc-btn doc-btn--primary" @click="viewAnalysis(duoConjoint1)">
                      Consulter
                    </button>
                    <button
                      v-if="analyseNumericId(duoConjoint1)"
                      type="button"
                      class="doc-btn doc-btn--ghost"
                      :disabled="downloadingId === analyseNumericId(duoConjoint1)"
                      @click="downloadPdf(duoConjoint1)"
                    >
                      {{ downloadingId === analyseNumericId(duoConjoint1) ? 'Téléchargement…' : 'Télécharger le PDF' }}
                    </button>
                  </div>
                </div>
              </article>
              <div v-else class="doc-card doc-card--empty">
                <ClientJourneyEmptyState
                  title="Bilan conjoint 1 en préparation"
                  message="Dès que le dossier du conjoint 1 est soumis et traité, le bilan apparaît ici. Comptez environ 72 h après soumission."
                  :active-step="1"
                  primary-to="/analyse-patrimoniale/mon-analyse"
                  primary-label="Continuer le formulaire"
                  secondary-to="/analyse-patrimoniale/contact"
                  secondary-label="Contacter mon conseiller"
                />
              </div>
            </div>

            <!-- Duo: Conjoint 2 -->
            <div v-if="typeOffre === 'duo' && activeTab === 'conjoint2'" class="analyses-docs">
              <div v-if="loadingDuo" class="doc-card doc-card--loading">Chargement du bilan conjoint 2…</div>
              <article v-else-if="duoConjoint2" class="doc-card">
                <div class="doc-card__thumb" aria-hidden="true">
                  <span class="doc-card__thumb-bar" />
                  <span class="doc-card__thumb-body" />
                </div>
                <div class="doc-card__body">
                  <span class="doc-badge" :class="statusBadgeClass(duoConjoint2)">
                    {{ statusLabel(duoConjoint2) }}
                  </span>
                  <h3 class="doc-card__title">
                    Analyse patrimoniale — conjoint 2 · {{ formatMonthYear(duoConjoint2.date_modification || duoConjoint2.date_creation) }}
                  </h3>
                  <p class="doc-card__meta">
                    Bilan individuel · mise à jour {{ relativeDate(duoConjoint2.date_modification || duoConjoint2.date_creation) }}
                  </p>
                  <div class="doc-card__actions">
                    <button type="button" class="doc-btn doc-btn--primary" @click="viewAnalysis(duoConjoint2)">
                      Consulter
                    </button>
                    <button
                      v-if="analyseNumericId(duoConjoint2)"
                      type="button"
                      class="doc-btn doc-btn--ghost"
                      :disabled="downloadingId === analyseNumericId(duoConjoint2)"
                      @click="downloadPdf(duoConjoint2)"
                    >
                      {{ downloadingId === analyseNumericId(duoConjoint2) ? 'Téléchargement…' : 'Télécharger le PDF' }}
                    </button>
                  </div>
                </div>
              </article>
              <div v-else class="doc-card doc-card--empty">
                <ClientJourneyEmptyState
                  title="Bilan conjoint 2 en préparation"
                  message="Dès que le dossier du conjoint 2 est soumis et traité, le bilan apparaît ici. Comptez environ 72 h après soumission."
                  :active-step="1"
                  primary-to="/analyse-patrimoniale/mon-analyse"
                  primary-label="Continuer le formulaire"
                  secondary-to="/analyse-patrimoniale/contact"
                  secondary-label="Contacter mon conseiller"
                />
              </div>
            </div>

            <!-- Duo: Couple -->
            <div v-if="typeOffre === 'duo' && activeTab === 'couple'" class="analyses-docs">
              <div v-if="loadingDuo" class="doc-card doc-card--loading">Chargement du bilan couple…</div>
              <div v-else-if="!duoCoupleReady" class="doc-card doc-card--empty">
                <ClientJourneyEmptyState
                  :title="duoCoupleEmptyTitle"
                  :message="duoCoupleEmptyMessage"
                  :active-step="duoCoupleEmptyStep"
                  primary-to="/analyse-patrimoniale/mon-analyse"
                  primary-label="Compléter les dossiers"
                  secondary-to="/analyse-patrimoniale/contact"
                  secondary-label="Contacter mon conseiller"
                />
              </div>
              <article v-else class="doc-card">
                <div class="doc-card__thumb" aria-hidden="true">
                  <span class="doc-card__thumb-bar" />
                  <span class="doc-card__thumb-body" />
                </div>
                <div class="doc-card__body">
                  <span class="doc-badge doc-badge--published">Publiée</span>
                  <h3 class="doc-card__title">Analyse patrimoniale — bilan couple</h3>
                  <p class="doc-card__meta">Vision ménage · agrégation des deux conjoints</p>
                  <div class="doc-card__actions">
                    <button type="button" class="doc-btn doc-btn--primary" @click="viewCoupleReport">
                      Consulter
                    </button>
                  </div>
                </div>
              </article>
            </div>

            <!-- Persona -->
            <div v-if="typeOffre !== 'duo'" class="analyses-docs">
              <div v-if="loading" class="doc-card doc-card--loading">Chargement de vos analyses…</div>
              <div v-else-if="!clientAnalyses.length" class="doc-card doc-card--empty">
                <ClientJourneyEmptyState
                  title="Votre bilan apparaîtra ici"
                  message="Après soumission de votre questionnaire, nos équipes préparent votre analyse patrimoniale. Vous êtes notifié par email sous environ 72 h."
                  :active-step="1"
                  primary-to="/analyse-patrimoniale/mon-analyse"
                  primary-label="Continuer mon analyse"
                  secondary-to="/analyse-patrimoniale/contact"
                  secondary-label="Contacter mon conseiller"
                />
              </div>
              <article
                v-for="(analyse, index) in clientAnalyses"
                v-else
                :key="analysisCardKey(analyse, index)"
                class="doc-card"
              >
                <div class="doc-card__thumb" aria-hidden="true">
                  <span class="doc-card__thumb-bar" />
                  <span class="doc-card__thumb-body" />
                </div>
                <div class="doc-card__body">
                  <span class="doc-badge" :class="statusBadgeClass(analyse)">
                    {{ statusLabel(analyse) }}
                  </span>
                  <h3 class="doc-card__title">
                    Analyse patrimoniale — {{ formatMonthYear(analyse.date_modification || analyse.date_creation) }}
                  </h3>
                  <p class="doc-card__meta">
                    Bilan complet · mise à jour {{ relativeDate(analyse.date_modification || analyse.date_creation) }}
                  </p>
                  <div class="doc-card__actions">
                    <button type="button" class="doc-btn doc-btn--primary" @click="viewAnalysis(analyse)">
                      Consulter
                    </button>
                    <button
                      v-if="analyseNumericId(analyse)"
                      type="button"
                      class="doc-btn doc-btn--ghost"
                      :disabled="downloadingId === analyseNumericId(analyse)"
                      @click="downloadPdf(analyse)"
                    >
                      {{ downloadingId === analyseNumericId(analyse) ? 'Téléchargement…' : 'Télécharger le PDF' }}
                    </button>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div v-else class="no-access">
            <p v-if="checkingDossierState">Vérification de votre dossier…</p>
            <p v-else-if="showCodeGate">Bienvenue ! Veuillez entrer le code pour accéder à vos analyses.</p>
            <p v-else>Vos résultats apparaîtront ici dès la publication de votre analyse.</p>
          </div>
        </div>

        <div v-if="!accessGranted && !checkingDossierState" class="popup-sup">
          <div v-if="showCodeGate" class="popup-content">
            <h2>Accès sécurisé</h2>
            <p>Pour protéger vos données patrimoniales, confirmez le code reçu par email.</p>
            <input
              v-model="accessCode"
              type="password"
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder="Ex. 482173"
              @keyup.enter="verifyCode"
            />
            <button type="button" class="validate-btn" :disabled="isLoading" @click="verifyCode">
              {{ isLoading ? 'Vérification…' : 'Confirmer' }}
            </button>
            <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          </div>

          <div v-else class="popup-content popup-content--waiting">
            <span class="popup-waiting__icon" aria-hidden="true">
              <i class="fas fa-hourglass-half"></i>
            </span>
            <h2>Résultats en préparation</h2>
            <p>
              Vos résultats apparaîtront ici dès la publication de votre analyse.
              Vous recevrez un email, puis un code de confirmation pour y accéder.
            </p>
            <router-link class="popup-waiting__cta" :to="waitingCtaTo">
              {{ waitingCtaLabel }}
            </router-link>
            <router-link class="popup-waiting__link" to="/analyse-patrimoniale/contact">
              Contacter mon conseiller
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <p v-else class="analyse-sup-role-hint">
      Cette page est réservée aux comptes client.
    </p>
  </div>
</template>

<script>
import axios from 'axios';
import { safeErrorMessage } from '@/utils/errorMessages';
import '@/assets/css/analyseSup.css';
import ClientJourneyEmptyState from '@/components/patrimoine/ClientJourneyEmptyState.vue';
import { getUserRole, isClientFacingRole } from '@/utils/auth';
import { getLocalItem, setLocalItem, removeLocalItem } from '@/utils/storage';
import { syncEmployeProfileFromApi } from '@/utils/permissions';
import { createSubmitGuard, formatThrottleMessage } from '@/utils/submitGuard';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { DOSSIER_STATE, fetchDossierState } from '@/utils/dossierState';

const verifyCodeGuard = createSubmitGuard(2500);
// localStorage (pas sessionStorage) : l'accès doit survivre à la fermeture de l'onglet/
// navigateur, dans la limite des 24h ci-dessous — sinon le client redemande le code à
// chaque nouvelle session alors que le serveur (bilan_code_validated_at) l'accepterait
// encore.
const BILAN_ACCESS_STORAGE_KEY = 'analysis_access_granted';
const BILAN_ACCESS_TTL_MS = 24 * 60 * 60 * 1000;

export default {
  components: {
    ClientJourneyEmptyState,
  },
  data() {
    return {
      accessCode: '',
      accessGranted: false,
      isLoading: false,
      clientAnalyses: [],
      loading: false,
      errorMessage: '',
      downloadingId: null,

      dossierState: DOSSIER_STATE.QUESTIONNAIRE,
      checkingDossierState: false,

      typeOffre: 'persona',
      activeTab: 'conjoint1',
      loadingDuo: false,
      duoConjoint1: null,
      duoConjoint2: null,
      duoMissing: null,
      duoLoadError: '',

      kpiReady: false,
      kpis: {
        valeurPatrimoine: null,
        soldeBudgetaire: null,
      },
    };
  },
  computed: {
    userRole() {
      return getUserRole() || '';
    },
    showClientUI() {
      return isClientFacingRole(this.userRole);
    },
    showCodeGate() {
      return this.dossierState === DOSSIER_STATE.PUBLISHED;
    },
    waitingCtaTo() {
      return this.dossierState === DOSSIER_STATE.REVIEW
        ? '/analyse-patrimoniale/mes-recommandations'
        : '/analyse-patrimoniale/mon-analyse';
    },
    waitingCtaLabel() {
      return this.dossierState === DOSSIER_STATE.REVIEW
        ? 'Voir l’état de mon dossier'
        : 'Continuer mon analyse';
    },
    duoCoupleReady() {
      if (this.duoMissing && (this.duoMissing.ordre_duo_1 || this.duoMissing.ordre_duo_2)) {
        return false;
      }
      return Boolean(this.duoConjoint1 && this.duoConjoint2);
    },
    duoCoupleIncompleteReason() {
      const missing1 = Boolean(this.duoMissing?.ordre_duo_1) || !this.duoConjoint1;
      const missing2 = Boolean(this.duoMissing?.ordre_duo_2) || !this.duoConjoint2;
      if (missing1 && missing2) return 'both';
      if (missing1) return 'c1';
      if (missing2) return 'c2';
      return 'unknown';
    },
    duoCoupleEmptyTitle() {
      return 'Bilan couple en attente';
    },
    duoCoupleEmptyMessage() {
      if (this.duoCoupleIncompleteReason === 'c1') {
        return 'Le bilan du conjoint 1 est encore requis avant d’afficher la vision couple. Après soumission, comptez environ 72 h de traitement.';
      }
      if (this.duoCoupleIncompleteReason === 'c2') {
        return 'Le bilan du conjoint 2 est encore requis avant d’afficher la vision couple. Après soumission, comptez environ 72 h de traitement.';
      }
      return 'Les bilans des deux conjoints sont nécessaires pour ouvrir le bilan couple. Chaque dossier traité apparaît ici sous environ 72 h.';
    },
    duoCoupleEmptyStep() {
      if (this.duoCoupleIncompleteReason === 'both') return 1;
      return 2;
    },
    documentsCount() {
      if (this.typeOffre === 'duo') {
        let n = 0;
        if (this.duoConjoint1) n += 1;
        if (this.duoConjoint2) n += 1;
        if (this.duoCoupleReady) n += 1;
        return n;
      }
      return this.clientAnalyses.length;
    },
  },
  async mounted() {
    await syncEmployeProfileFromApi();
    if (!this.showClientUI) return;
    if (this.hasStoredBilanAccess()) {
      this.accessGranted = true;
      await this.loadClientOffer();
      await this.loadResults();
      return;
    }
    await this.refreshDossierState();
  },
  methods: {
    async refreshDossierState() {
      if (!this.getValidToken()) return;
      this.checkingDossierState = true;
      try {
        const { state } = await fetchDossierState();
        this.dossierState = state;
      } catch (error) {
        if (error.response?.status !== 403) {
          console.error('refreshDossierState:', error);
        }
        this.dossierState = DOSSIER_STATE.PUBLISHED;
      } finally {
        this.checkingDossierState = false;
      }
    },
    hasStoredBilanAccess() {
      if (!this.getValidToken()) return false;
      const raw = getLocalItem(BILAN_ACCESS_STORAGE_KEY);
      if (!raw) return false;
      const grantedAt = Number(raw);
      if (!Number.isFinite(grantedAt) || grantedAt <= 0) {
        removeLocalItem(BILAN_ACCESS_STORAGE_KEY);
        return false;
      }
      if (Date.now() - grantedAt > BILAN_ACCESS_TTL_MS) {
        removeLocalItem(BILAN_ACCESS_STORAGE_KEY);
        return false;
      }
      return true;
    },

    grantBilanAccessSession() {
      setLocalItem(BILAN_ACCESS_STORAGE_KEY, String(Date.now()));
    },

    revokeBilanAccessSession() {
      removeLocalItem(BILAN_ACCESS_STORAGE_KEY);
    },

    handleBilanAccessRevoked(message) {
      this.accessGranted = false;
      this.revokeBilanAccessSession();
      this.dossierState = DOSSIER_STATE.PUBLISHED;
      this.errorMessage = message || 'Entrez le code reçu par email pour accéder à vos bilans.';
    },
    getValidToken() {
      try {
        return isAuthenticatedSession() ? '1' : null;
      } catch (_) {
        return null;
      }
    },

    async verifyCode() {
      const normalizedCode = String(this.accessCode || '').replace(/\s+/g, '');
      if (!/^\d{6}$/.test(normalizedCode)) {
        this.errorMessage = 'Code invalide. Entrez les 6 chiffres reçus par email.';
        return;
      }
      if (!this.getValidToken()) {
        this.errorMessage = 'Session invalide. Veuillez vous reconnecter.';
        return;
      }

      const guarded = await verifyCodeGuard.run(async () => {
        this.isLoading = true;
        this.errorMessage = '';
        try {
          const { data } = await axios.post(
            '/api/verify-code-validation/',
            { code: normalizedCode },
            { withCredentials: true }
          );

          if (data && data.success) {
            this.accessGranted = true;
            this.grantBilanAccessSession();
            await this.loadClientOffer();
            await this.loadResults();
          } else {
            this.errorMessage = data?.error || 'Code incorrect';
          }
        } catch (error) {
          if (error.response?.data?.code === 'bilan_code_required') {
            this.handleBilanAccessRevoked('Code de validation requis pour accéder aux bilans.');
          } else {
            this.errorMessage =
              formatThrottleMessage(error) ||
              (error.response?.status === 401
                ? 'Session expirée. Veuillez vous reconnecter.'
                : error.response?.data?.error || 'Erreur lors de la vérification du code');
          }
        } finally {
          this.isLoading = false;
        }
      });

      if (!guarded.ok) {
        this.errorMessage = 'Veuillez patienter avant de réessayer.';
      }
    },

    formatMoney(value) {
      const n = Number(value || 0);
      const abs = Math.abs(n);
      const formatted = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(abs);
      return (n < 0 ? '-' : '') + formatted;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    },

    formatMonthYear(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      const s = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      return s.charAt(0).toUpperCase() + s.slice(1);
    },

    relativeDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '—';
      const days = Math.floor((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000));
      if (days <= 0) return "aujourd'hui";
      if (days === 1) return 'il y a 1 jour';
      if (days < 30) return `il y a ${days} jours`;
      return `le ${this.formatDate(dateString)}`;
    },

    statusLabel(analyse) {
      const raw = String(
        analyse?.recommandation_ia_statut_display ||
          analyse?.statut ||
          analyse?.recommandation_ia_statut ||
          ''
      ).trim();
      if (!raw) return 'Publiée';
      const lower = raw.toLowerCase();
      if (lower === 'publiee' || lower === 'publiée') return 'Publiée';
      if (lower === 'en_attente' || lower === 'en attente') return 'En revue';
      if (lower === 'brouillon') return 'Brouillon';
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    },

    statusBadgeClass(analyse) {
      const label = this.statusLabel(analyse).toLowerCase();
      if (label.includes('publi')) return 'doc-badge--published';
      if (label.includes('revue') || label.includes('attente')) return 'doc-badge--review';
      return 'doc-badge--muted';
    },

    kpiMetaClass(value) {
      return value != null ? 'analyses-kpi__meta--ok' : 'analyses-kpi__meta--muted';
    },

    analyseNumericId(analyse) {
      const id = analyse?.id ?? analyse?.analyse_id;
      const n = Number(id);
      return Number.isFinite(n) && n > 0 ? n : null;
    },

    async fetchKpis() {
      this.kpiReady = false;
      try {
        if (this.typeOffre === 'duo') {
          const res = await axios.get('/api/analyse-patrimoniale/duo-bilan/', {
            withCredentials: true,
          });
          const menage = res.data?.menage || {};
          const budget = menage?.budget || {};
          const patrimoine = menage?.patrimoine || {};
          this.kpis = {
            valeurPatrimoine: Math.round(Number(patrimoine.valeur_patrimoine) || 0) || null,
            soldeBudgetaire: Math.round(Number(budget.solde_annuel) || 0),
          };
          if (this.kpis.valeurPatrimoine === 0 && !patrimoine.valeur_patrimoine) {
            this.kpis.valeurPatrimoine = null;
          }
        } else {
          const res = await axios.get('/api/analyse-patrimoniale/', {
            withCredentials: true,
            params: { latest: 'true' },
          });
          const stats = res.data?.stats ?? {};
          const vp = stats.valeur_patrimoine;
          const solde = stats.solde_budgetaire;
          this.kpis = {
            valeurPatrimoine: vp != null && vp !== '' ? Math.round(Number(vp) || 0) : null,
            soldeBudgetaire: solde != null && solde !== '' ? Math.round(Number(solde) || 0) : null,
          };
        }
        this.kpiReady = true;
      } catch (error) {
        if (error.response?.data?.code === 'bilan_code_required') {
          this.handleBilanAccessRevoked();
        }
        this.kpiReady = true;
        this.kpis = { valeurPatrimoine: null, soldeBudgetaire: null };
      }
    },

    async fetchClientAnalyses() {
      this.loading = true;
      try {
        const token = this.getValidToken();
        if (!token) return;
        const response = await axios.get('/api/analyse-patrimoniale/', {
          withCredentials: true,
        });

        const raw = response.data;
        const list = raw?.analyses ?? raw?.results;
        this.clientAnalyses = Array.isArray(list) ? list : [];
      } catch (error) {
        console.error('Erreur lors du chargement des analyses:', error);
        if (error.response?.data?.code === 'bilan_code_required') {
          this.handleBilanAccessRevoked();
        } else if (error.response?.status === 401) {
          this.errorMessage = 'Session expirée. Veuillez vous reconnecter.';
        } else {
          // Pas de error.response?.data?.detail en repli : ce champ vient tel quel du
          // serveur (souvent en anglais / jargon technique de l'API), pas rédigé pour
          // être lu par le client.
          this.errorMessage = 'Une erreur est survenue lors du chargement des données. Réessayez dans quelques instants.';
        }
      } finally {
        this.loading = false;
      }
    },

    async loadClientOffer() {
      try {
        const token = this.getValidToken();
        if (!token) return;
        const { data } = await axios.get('/api/all-clients/', {
          withCredentials: true,
        });
        this.typeOffre = String(data?.type_offre || 'persona').toLowerCase();
      } catch (error) {
        console.error('loadClientOffer:', error);
        this.duoLoadError = safeErrorMessage(
          error,
          "Impossible de déterminer votre type d'offre. Les bilans Persona sont affichés par défaut."
        );
        this.typeOffre = 'persona';
      }
    },

    async fetchLatestAnalyseDuo(ordre) {
      const token = this.getValidToken();
      if (!token) return null;
      try {
        const { data } = await axios.get('/api/analyse-patrimoniale/', {
          params: { latest: 'true', ordre_duo: ordre },
          withCredentials: true,
        });
        return data?.details || null;
      } catch (error) {
        if (error.response?.data?.code === 'bilan_code_required') {
          this.handleBilanAccessRevoked();
        }
        throw error;
      }
    },

    async fetchDuoMenage() {
      const token = this.getValidToken();
      if (!token) return null;
      try {
        const { data } = await axios.get('/api/analyse-patrimoniale/duo-bilan/', {
          withCredentials: true,
        });
        return data || null;
      } catch (error) {
        if (error.response?.data?.code === 'bilan_code_required') {
          this.handleBilanAccessRevoked();
        }
        throw error;
      }
    },

    async loadResults() {
      if (this.typeOffre === 'duo') {
        this.loadingDuo = true;
        this.duoLoadError = '';
        const partialErrors = [];
        try {
          let a1 = null;
          let a2 = null;
          let duo = null;
          try {
            a1 = await this.fetchLatestAnalyseDuo(1);
          } catch (error) {
            console.error('fetchLatestAnalyseDuo(1):', error);
            partialErrors.push('conjoint 1');
          }
          try {
            a2 = await this.fetchLatestAnalyseDuo(2);
          } catch (error) {
            console.error('fetchLatestAnalyseDuo(2):', error);
            partialErrors.push('conjoint 2');
          }
          try {
            duo = await this.fetchDuoMenage();
          } catch (error) {
            console.error('fetchDuoMenage:', error);
            partialErrors.push('bilan couple');
          }
          this.duoConjoint1 = a1;
          this.duoConjoint2 = a2;
          this.duoMissing = duo?.missing || null;
          if (partialErrors.length === 3) {
            this.duoLoadError = 'Impossible de charger vos bilans Duo. Vérifiez votre connexion et réessayez.';
          } else if (partialErrors.length > 0) {
            this.duoLoadError = `Certaines données n'ont pas pu être chargées (${partialErrors.join(', ')}). Réessayez dans quelques instants.`;
          }
        } finally {
          this.loadingDuo = false;
        }
      } else {
        await this.fetchClientAnalyses();
      }
      await this.fetchKpis();
    },

    analysisCardKey(analyse, index) {
      if (analyse?.ordre_duo != null) return `ordre-${analyse.ordre_duo}`;
      if (analyse?.id != null) return `id-${analyse.id}`;
      if (analyse?.date_modification) return `mod-${analyse.date_modification}`;
      if (analyse?.client_login) return `login-${analyse.client_login}-${index}`;
      return `analyse-${index}`;
    },

    async viewAnalysis(ref) {
      const ordre =
        ref != null && typeof ref === 'object' && ref.ordre_duo != null
          ? Number(ref.ordre_duo)
          : null;
      const legacyId =
        ref != null && typeof ref !== 'object'
          ? String(ref).trim()
          : this.analyseNumericId(ref)
            ? String(this.analyseNumericId(ref))
            : '';
      if (ordre === 1 || ordre === 2) {
        await this.$router.push({
          name: 'Rapport Patrimonial',
          params: { id: 'latest' },
          query: { ordre_duo: String(ordre) },
        });
        return;
      }
      if (legacyId && legacyId !== 'null') {
        await this.$router.push({
          name: 'Rapport Patrimonial',
          params: { id: legacyId },
        });
        return;
      }
      await this.$router.push({
        name: 'Rapport Patrimonial',
        params: { id: 'latest' },
      });
    },

    async viewCoupleReport() {
      await this.$router.push({ name: 'Rapport Patrimonial Duo' });
    },

    async downloadPdf(analyse) {
      const id = this.analyseNumericId(analyse);
      if (!id) return;
      this.downloadingId = id;
      try {
        const response = await axios.get(`/api/analyse-patrimoniale/${id}/generate_pdf/`, {
          responseType: 'blob',
          withCredentials: true,
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analyse_patrimoniale_${id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        if (error.response?.data?.code === 'bilan_code_required') {
          this.handleBilanAccessRevoked();
        } else {
          this.errorMessage = 'Impossible de télécharger le PDF pour le moment.';
        }
      } finally {
        this.downloadingId = null;
      }
    },
  },
};
</script>

<style scoped>
.analyses-page__inner {
  width: 100%;
}

.analyses-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.analyses-kpi {
  background: var(--client-surface-card, #f7f9fc);
  border: 1px solid var(--client-line, rgba(1, 38, 62, 0.1));
  border-radius: var(--client-radius, 20px);
  box-shadow: var(--client-shadow, 0 1px 2px rgba(1, 38, 62, 0.03));
  padding: var(--space-5);
  min-height: 120px;
}

.analyses-kpi__label {
  margin: 0 0 var(--space-2);
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--client-text-muted, #64748b);
}

.analyses-kpi__value {
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(1.35rem, 2.2vw, 1.75rem);
  font-weight: 600;
  line-height: 1.2;
  color: var(--brand-navy);
}

.analyses-kpi__meta {
  margin: var(--space-2) 0 0;
  font-family: var(--font-body);
  font-size: 0.8125rem;
  color: var(--client-text-muted, #64748b);
}

.analyses-kpi__meta--ok {
  color: var(--success, #2d8659);
}

.analyses-kpi__meta--warn {
  color: var(--brand-brown);
}

.analyses-kpi__meta--muted {
  color: var(--client-text-muted, #64748b);
}

.analyses-kpi__meta--link a {
  color: var(--success, #2d8659);
  text-decoration: none;
  font-weight: 500;
}

.analyses-kpi__meta--link a:hover {
  text-decoration: underline;
}

.duo-tabs {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.25rem;
  margin: 0 0 var(--space-5);
  background: var(--paper);
  border: 1px solid var(--client-line, rgba(1, 38, 62, 0.1));
  border-radius: var(--radius-pill);
  max-width: 520px;
}

.duo-tab {
  flex: 1 1 0;
  border: 0;
  background: transparent;
  color: var(--brand-navy);
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.55rem 0.75rem;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.duo-tab:hover {
  background: rgba(1, 38, 62, 0.04);
}

.duo-tab.active {
  background: var(--brand-navy);
  color: var(--paper);
}

.analyses-docs {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.doc-card {
  display: flex;
  gap: var(--space-5);
  align-items: flex-start;
  background: var(--paper);
  border: 1px solid var(--client-line, rgba(1, 38, 62, 0.1));
  border-radius: var(--client-radius-lg, 24px);
  box-shadow: var(--client-shadow, 0 1px 2px rgba(1, 38, 62, 0.03));
  padding: var(--space-5);
}

.doc-card--loading,
.doc-card--empty {
  display: block;
  color: var(--client-text-muted);
  font-family: var(--font-body);
}

.doc-card--empty:hover {
  transform: none;
}

.doc-card__thumb {
  flex: 0 0 auto;
  width: 72px;
  height: 92px;
  border-radius: 10px;
  border: 1px solid var(--client-line);
  background: #f3f0e8;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.doc-card__thumb-bar {
  display: block;
  height: 22px;
  background: var(--brand-navy);
}

.doc-card__thumb-body {
  flex: 1;
  margin: 10px 8px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--brand-gold) 35%, #fff);
}

.doc-card__body {
  flex: 1;
  min-width: 0;
}

.doc-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.65rem;
  border-radius: var(--radius-pill);
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: var(--space-2);
}

.doc-badge::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.doc-badge--published {
  background: color-mix(in srgb, var(--success) 14%, #fff);
  color: var(--success);
}

.doc-badge--review {
  background: var(--brand-gold-soft);
  color: var(--brand-gold-dark);
}

.doc-badge--muted {
  background: rgba(1, 38, 62, 0.06);
  color: var(--muted);
}

.doc-card__title {
  margin: 0 0 var(--space-2);
  font-family: var(--font-display);
  font-size: clamp(1.15rem, 2vw, 1.4rem);
  font-weight: 600;
  color: var(--brand-navy);
  line-height: 1.3;
}

.doc-card__meta {
  margin: 0 0 var(--space-4);
  font-family: var(--font-body);
  font-size: 0.875rem;
  color: var(--client-text-muted, #64748b);
}

.doc-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.doc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0.65rem 1.35rem;
  border-radius: var(--radius-pill);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.doc-btn--primary {
  background: var(--brand-gold);
  border: 1px solid var(--brand-gold);
  color: var(--brand-navy);
}

.doc-btn--primary:hover {
  background: var(--brand-gold-light);
  border-color: var(--brand-gold-light);
}

.doc-btn--ghost {
  background: transparent;
  border: 1px solid var(--brand-navy);
  color: var(--brand-navy);
}

.doc-btn--ghost:hover:not(:disabled) {
  background: rgba(1, 38, 62, 0.04);
}

.doc-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  color: var(--brand-red);
  margin-top: 10px;
}

.analyse-sup-role-hint {
  padding: 1.5rem;
  color: var(--muted);
  font-family: var(--font-body);
}

@media (max-width: 900px) {
  .analyses-kpis {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .doc-card {
    flex-direction: column;
  }

  .doc-card__actions {
    flex-direction: column;
  }

  .doc-btn {
    width: 100%;
  }
}
</style>
